/*
 * TunePad
 *
 * Michael S. Horn
 * Northwestern University
 * michael-horn@northwestern.edu
 *
 * This project was funded by the National Science Foundation (grant DRL-1612619).
 * Any opinions, findings and conclusions or recommendations expressed in this
 * material are those of the author(s) and do not necessarily reflect the views
 * of the National Science Foundation (NSF).
 */
import styles from './codemirror.module.css' with {type: 'css'};
import html from './codemirror.module.html';
import errorIcon from './error-icon.svg';
import warnIcon from './warn-icon.svg';
import musicIcon from './music-icon.svg';
import traceIcon from './trace-icon.svg';

// Codemirror core and extensions
import { EditorView, basicSetup } from "codemirror";
import { keymap} from "@codemirror/view"
import { python } from "@codemirror/lang-python";
import { indentUnit, LanguageSupport } from "@codemirror/language";
import { autocompletion } from "@codemirror/autocomplete";
import { indentWithTab, cursorLineEnd, insertNewlineAndIndent, insertNewlineKeepIndent } from "@codemirror/commands"
import { gutter, GutterMarker } from "@codemirror/view";
import { EditorState, StateField, StateEffect, RangeSet, Compartment, Extension, Transaction } from "@codemirror/state";

// Local themes and autocompelete
import { tunePadLight } from './light-theme';
import { tunePadDark } from './dark-theme';
import { globalCompletion } from "./autocomplete";


/**
 * Used to add gutter markers
 */
export interface Marker {
    line : number,     // line number (int)
    message? : string  // optional hover message
}


/**
 * TunePad python code editor
 * 
 * ```html
 * <python-editor
 *    readonly = "{{ true | false }}"
 *    theme = "{{ dark | light }}"
 *    font-size = "13px">
 * 
 * # default python code goes here...
 * for i in range(30):
 *     playNote(30)
 * </python-editor>
 * ```
 */
export class PythonEditor extends HTMLElement {

    static DefineElement() {
        if (customElements.get('python-editor') === undefined) {
            customElements.define("python-editor", PythonEditor);
        }
    }

    static observedAttributes = [
        "readonly",  // boolean string, default is false   
        "theme",     // "dark" | "light" (default is dark)
        "font-size"  // e.g. 13px
    ];

    // color theme ( "dark" | "light" )
    private theme = "dark";
    private themeState = new Compartment();

    // readonly?
    private readonly : boolean = false;
    private readonlyState = new Compartment();

    // font-size
    private fontSize = "13px";
    private fontSizeState = new Compartment();

    // Yjs collaboration binding
    private collabState = new Compartment();

    // codemirror state object
    private state ? : EditorState;

    // codemirror view object
    private view ? : EditorView;

    /// all of the HTML elements for the instrument are contained within a shadow DOM
    private root : ShadowRoot;

    public get code() : string { return this.view?.state.doc.toString() || ''; }

    // if defined, use this starter text to initialize codemirror
    private _initialContent? : string;
    set initialContent(s : string) { this._initialContent = s; }

    private language : LanguageSupport = python();


    constructor() {
        super();
        this.root = this.attachShadow({ mode: 'open' });
        this.root.adoptedStyleSheets.push(styles);
        this.root.innerHTML = html;
        this.addClass('.codemirror-wraper', this.theme);
    }


    async connectedCallback() {
        await this.waitForWindowLoad();

        const defaultCode = this.innerHTML
            //.trim()
            .split('\n')
            .map((line) => line.startsWith('/') ? line.substring(1) : line)  // ugly markdown hack
            .join('\n')
            .replaceAll('&lt;', '<')
            .replaceAll('&gt;', '>')
            //.concat('\n'.repeat(8));  // end with 8 lines of white space

        const extensions = [
            ... this.extensions,
            this.fontSizeState.of(this.generateFontSizeTheme(this.fontSize)),
            this.readonlyState.of(EditorState.readOnly.of(this.readonly)),
            this.themeState.of(this.theme === 'dark' ? [ tunePadDark ] : [ tunePadLight ]),
            this.collabState.of([]),
        ];

        this.state = EditorState.create({
            extensions: extensions,
            doc: this._initialContent ?? defaultCode
        });
        
        const parent = this.root.querySelector('#codemirror-editor');
        this.view = new EditorView({ state: this.state, parent: parent! });
    }


    disconnectedCallback() {
        this.unbindCollaboration();
        this.view?.destroy();
    }


    attributeChangedCallback(name : string, oldValue : string, newValue : string) {
        if (newValue === oldValue) return;
        if (name === 'readonly') {
            this.readonly = (newValue === 'true');
            this.view?.dispatch({
                effects: this.readonlyState.reconfigure(EditorState.readOnly.of(this.readonly))
            });
        }
        else if (name === 'theme' && ['light', 'dark', 'bitcrush'].includes(newValue)) {
            this.removeClass('.codemirror-wraper', this.theme);
            this.theme = newValue;
            this.addClass('.codemirror-wraper', this.theme);
            this.view?.dispatch({
                effects: this.themeState.reconfigure(
                    newValue === 'dark' ? [ tunePadDark ] : [ tunePadLight ])
            });
        }
        else if (name === 'font-size') {
            this.fontSize = newValue;
            this.view?.dispatch({
                effects: this.fontSizeState.reconfigure(this.generateFontSizeTheme(newValue))
            });
        }
    }

    /**
     * Replace all text content
     */
    public replaceCode(code : string) {
        if (this.isConnected) {
            this.view?.dispatch({
                changes: {
                    from: 0,
                    to: this.view!.state.doc.length,
                    insert: code
                }
            });
        }
        else {
            this.initialContent = code;
        }
    }


    public insertCodeInNewLine(code : string){
        if(this.view){
            let pos = this.view.state.selection.main.head;
            let line = this.view.state.doc.lineAt(pos);

            this.view.dispatch({ selection: { anchor: line.to } });

            insertNewlineAndIndent({
                state: this.view.state,
                dispatch: transaction => { this.view!.update([ transaction ]) }
            });

            this.view.dispatch({ changes: { from: this.view.state.selection.main.head, insert: code }});
            cursorLineEnd(this.view);
        }
    }


    /**
     * Turn on collaborative editing by binding to a Y.Text object
     */
    public bindCollaboration(collab : Extension[]) {
        this.view?.dispatch({ effects: this.collabState.reconfigure(collab) });
    }

    public unbindCollaboration() {
        this.view?.dispatch({ effects: this.collabState.reconfigure([]) });
    }


    /**
     * Set markers in the gutter. Will first clear existing markers of this type
     */
    public setMarkers(marks : Marker[], type : "error" | "warning" | "trace") {
        this.clearMarkers(type);
        marks.forEach((mark) => {
            this.setMarker(mark, type);
        });
    }


    /**
     * Clear all gutter markers of a given type
     */
    public clearMarkers(type: "error" | "warning" | "trace") {
        this.view?.dispatch({ effects : infoEffect.of( { pos: -1, type : type, message : '' }) });
    }


    /**
     * Add a single gutter marker of a given type
     */
    public setMarker(mark : Marker, type: "error" | "warning" | "trace") {
        if (this.view && mark.line > 0 && mark.line <= this.view.state.doc.lines) {
            const line = this.view?.state.doc.line(mark.line);
            if (line) {
                this.view?.dispatch({ effects: infoEffect.of({ 
                    pos : line.from,
                    type : type,
                    message: (mark.message || '')
                }) });
            }
        }
    }



    /**
     * A list of errors to show below the editor
     */
    set errors(errs : PythonError[]) {
        this.setHTML('#errors', '');
        errs.forEach((err) => {
            let m = `<b>${err.name}:</b> ${err.message.replaceAll('<', '&lt;')}`;
            if (err.line > 0) m += ` on line ${err.line}`;
            const el = document.createElement('python-message');
            el.setAttribute('type', 'error');
            if (err.details) el.setAttribute('details', err.details);
            el.innerHTML = m;
            this.root.querySelector('#errors')?.appendChild(el);
        });

        // error markers in the editor gutter
        const markers = errs
            .filter(err => err.line > 0)
            .map((err) => { 
                return { line: err.line, message: `${err.name}: ${err.message}` }
            });
        this.setMarkers(markers, 'error');
        this.classList.toggle('errors', errs.length > 0);
    }


    /**
     * A list of warnings to show below the editor
     */
    set warnings(warns : PythonError[]) {
        this.setHTML('#warnings', '');
        warns.forEach((warn) => {
            let m = `<b>${warn.name}:</b> ${warn.message}`;
            if (warn.line > 0) m += ` on line ${warn.line}.`;
            if (warn.details) m += `<br>${warn.details}`;
            const el = document.createElement('python-message');
            el.setAttribute('type', 'warning');
            el.innerHTML = m;
            this.root.querySelector('#warnings')?.appendChild(el);
        });
        // warning markers in the editor gutter
        const markers = warns
            .filter(warn => warn.line > 0)
            .map(warn => { 
                return { line: warn.line, message: `${warn.name}: ${warn.message}` }
            });
        this.setMarkers(markers, 'warning');
        this.classList.toggle('warnings', warns.length > 0);
    }

    /**
     * A list of unit test results to show below the editor
     */
    /*
    set unitTests(tests : { message : string, pass : boolean }[]) {
        this.setHTML('#tests', '');
        tests.forEach((test) => {
            const el = document.createElement('python-message');
            el.setAttribute('type', 'test');
            el.setAttribute('pass', `${test.pass}`);
            el.innerHTML = test.message;
            this.root.querySelector('#tests')?.appendChild(el);
        });
    }
    */
    /**
     * A list of messages to show below the editor
     */
    /*
    set messages(msgs : string[]) {
        this.setHTML('#messages', '');
        msgs.forEach((msg) => {
            const el = document.createElement('python-message');
            el.setAttribute('type', 'message');
            el.innerHTML = msg;
            this.root.querySelector('#messages')?.appendChild(el);            
        })
    }
*/

    /**
     * A list of print outputs to show below the editor
     */
    /*
    set output(out : string[]) {
        this.setHTML('#output', '');
        if (out.length > 0) {
            const el = document.createElement('python-output');
            el.innerHTML = out.join('\n');
            this.root.querySelector('#output')?.appendChild(el);
        }
    }
    */

    private emitEvent(evt : string) {
        const ce = new CustomEvent("codemirror-event", {
            bubbles: false,
            composed: true,
            detail: { event: evt }
        });
        this.root.host.dispatchEvent(ce);
    }


    /**
     * Listen for user events that might trigger a recompile
     */
    private _lines = -1; 
    private _lineAt = -1;

    private eventHandlers = EditorView.domEventHandlers({
        keyup: (event, view) => {
            let fired = false;
            // Line count changed
            let lines = view.state.doc.lines;

            if (this._lines >= 0 && lines > this._lines) {
                fired = true;
                this.emitEvent("lines-inserted");
            }
            else if (this._lines >= 0 && lines < this._lines) {
                fired = true;
                this.emitEvent("lines-removed");
            }
            this._lines = lines;

            // Cursor moved to a different line
            let pos = view.state.selection.main.head;
            let lineAt = view.state.doc.lineAt(pos).number;
            if (this._lineAt >= 0 && this._lineAt !== lineAt && !fired) {
                this.emitEvent("cursor-moved");
            }
            this._lineAt = lineAt;
        },
        pointerup: (event, view) => {
            // check to see if the cursor moved to a new line
            let pos = view.state.selection.main.head;
            let lineAt = view.state.doc.lineAt(pos).number;
            if (this._lineAt >= 0 && this._lineAt !== lineAt) {
                this.emitEvent("cursor-moved");
            }
            this._lineAt = lineAt;
        },
        // Editor blur
        blur: (event, view) => {
            this._lines = -1;
            this._lineAt = -1;
            this.emitEvent("editor-blur");
        }
    });


    // our set of codemirror exensions
    private extensions = [
        infoGutter,
        basicSetup,
        this.language,
        keymap.of([indentWithTab]),
        this.eventHandlers,
        EditorView.updateListener.of(update => { 
            if (update.docChanged) { this.emitEvent('content-changed'); } 
        }),
        indentUnit.of("    "),
        autocompletion({override: [globalCompletion]})
    ];


    public blockDropped(pointerX: number, pointerY: number, code: string) {
        const r = this.getBoundingClientRect();
        const inside = pointerX >= r.left && pointerX <= r.right && pointerY >= r.top && pointerY <= r.bottom;

        if(inside){
            this.insertBlockCode(pointerX, pointerY, code);
            this.emitEvent("lines-inserted");
        }
    }

    private insertBlockCode(pointerX : number, pointerY: number, code : string) {
        if(this.view === undefined) return;

        const view = this.view;

        const insertPoint = view.posAtCoords({
            x: pointerX,
            y: pointerY
        }, false);
        const line = view.state.doc.lineAt(insertPoint);

        this.view.dispatch({ selection: { anchor: line.to } });
        if (line.text.trim().length > 0) {
            insertNewlineAndIndent({
                state: view.state,
                dispatch: transaction => { view.update([ transaction ]) }
            });
        }
        const lines = code.split('\n');
        for (let i=0; i<lines.length; i++) {
            let l = lines[i];
            if (l.startsWith('\\t')) {
                insertNewlineAndIndent({
                    state: view.state,
                    dispatch: transaction => { view.update([ transaction ]) }
                });
                cursorLineEnd(view);
                l = l.substring(2);
            }
            else if (i > 0) {
                insertNewlineKeepIndent({
                    state: view.state,
                    dispatch: transaction => { view.update([ transaction ]) }
                });
                cursorLineEnd(view);
            }
            view.dispatch({ changes: { from: view.state.selection.main.head, insert: l }});
            cursorLineEnd(view);
        }
    }

    private generateFontSizeTheme(fontSize : string) {
        return EditorView.theme({
            '&': { fontSize: "16px" },
            ".cm-content": { fontSize },
            ".cm-gutters": { fontSize },
            ".cm-scroller": { fontSize },
        });
    }


    private setHTML(selector : string, html : string) {
        const e = this.root.querySelector(selector);
        if (e) e.innerHTML = html;
    }

    private addClass(selector : string, className : string) {
        const e = this.root.querySelector(selector);
        if (e) e.classList.add(className);
    }

    private removeClass(selector : string, className : string) {
        const e = this.root.querySelector(selector);
        if (e) e.classList.remove(className);
    }

    private async waitForWindowLoad() : Promise<void> {
        return new Promise((resolve) => {
            if (document.readyState === 'complete') {
                resolve();
            } else {
                window.addEventListener('load', () => {
                    resolve();
                });
            }
        });
    }
}


// Error Gutteer

class InfoMarker extends GutterMarker {

    message = '';
    type = '';

    constructor(type: string, message: string) {
        super();
        this.message = message;
        this.type = type;
    }

    toDOM() {
        const div = document.createElement("div");
        div.innerText = "⬤";
        if (this.type === "error") {
            div.innerHTML = errorIcon;
            div.className = 'errorMarker';
            div.setAttribute("data-tooltip", this.message);
        } else if (this.type === "warning") {
            div.innerHTML = warnIcon;
            div.className = 'warningMarker';
            div.setAttribute("data-tooltip", this.message);
        } else if (this.type === "trace") {
            div.innerHTML = traceIcon;
            div.className = 'traceMarker';
        }
        return div;
    }
}

const infoEffect = StateEffect.define<{pos: number, type: string, message: string}>({
    map: (val, mapping) => ({pos: mapping.mapPos(val.pos), type: val.type, message: val.message})
});

const infoState = StateField.define<RangeSet<InfoMarker>>({
    create() { return RangeSet.empty },
    update(set, transaction) {
        set = set.map(transaction.changes);
        for (let e of transaction.effects) {
            if (e.is(infoEffect)) {
                if (e.value.pos < 0) {
                    set = set.update({ filter: (from, to, value) => (value.type !== e.value.type) });
                } else {
                    const marker = new InfoMarker(e.value.type, e.value.message);
                    set = set.update({add: [marker.range(e.value.pos)]});
                }
            }
        }
        return set;
    }
});

const infoGutter = [
    infoState,
    gutter({
        class: "cm-info-gutter",
        markers: v => v.state.field(infoState),
        initialSpacer: () => new InfoMarker('', ''),
    })
];


interface PythonError {

    /// name (python type) of the syntax or runtime error
    name: string,

    /// description of the error
    message: string,

    /// details like stack trace, etc
    details ? : string,

    /// line number of the error (-1 means no line number information available)
    line: number,

    /// target cell ID that this error or warning applies to (-1 means the current cell)
    target ? : string,

    /// cell ID that generated this error or warning
    source ? : string
}